import store from '@/store/index';
import {
  VuexModule,
  Module,
  getModule,
  Mutation,
  Action,
} from 'vuex-module-decorators';
import { IntegratorMovementsModel, IntegratorMovement, IntegratorMovementFilter } from './integrator-movement-types';
import { initializeFilter } from "./integrator-movement-factory";
import Formatters from '@/shared/formatters/formatters';
import moment from 'moment';
import IntegratorMovementService from '@/services-http/sdi/IntegratorMovementService';

const formatter = new Formatters();

@Module({
  namespaced: true,
  name: 'integrator-movement',
  dynamic: true,
  store,
})
class IntegratorMovementsStore extends VuexModule {
  data: IntegratorMovementsModel = new IntegratorMovementsModel();

  get integratorMovementFilter() {
    return this.data.integratorMovementFilter;
  }

  get filterWithFilledFields() {
    return formatter.getFilledKeysFromObject(this.integratorMovementFilter);
  }

  get integratorMovements() {
    return this.data.integratorMovements;
  }

  get loadPage() {
    return this.data.loadingPage;
  }

  get automaticSearchFields() {
    return [
      'page',
      'size',
    ];
  }

  get totalPages() {
    return this.data.totalPages;
  }

  get totalElements() {
    return this.data.totalElements;
  }

  @Action
  async fetchIntegratorMovements() {
    if (!Object.keys(this.filterWithFilledFields).includes('createdDateStart')) return;

    const integratorMovementService = new IntegratorMovementService();
    let params = { ...this.filterWithFilledFields };

    params.page--;


    const paramString = Object.entries(params)
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
      .join('&');

    const response = await integratorMovementService.FetchIntegrationMovements(paramString);

    const result = response.data.content as IntegratorMovement[];

    this.SET_INTEGRATOR_MOVEMENTS(result);
    this.SET_TOTAL_PAGES(response.data.totalPages);
    this.SET_TOTAL_ELEMENTS(response.data.totalElements);
  }

  @Action
  async search() {
    this.loadingPage(true);

    try {
      await this.fetchIntegratorMovements();
    } catch(error) {
      return error;
    } finally {
      this.loadingPage(false);
    }
  }

    @Action
  async resend(integratorMovementId: number) {
    try {
      this.loadingPage(true);

      const integratorMovementService = new IntegratorMovementService();

      return await integratorMovementService.ResendMovement(integratorMovementId)
        .then(() => true)
        .catch(() => false);
    } catch(error) {
      return false;
    } finally {
      this.loadingPage(false);
    }
  }

  @Action
  async resetFilter() {
    this.RESET_FILTER();

    await this.search();
  }

  @Action
  async setFilter(filter: Partial<IntegratorMovementFilter>) {
    this.SET_FILTER(filter);

    if (this.automaticSearchFields.some((key) => key in filter)) {
      await this.search();
    }
  }

  @Mutation
  loadingPage(loading: boolean) {
    this.data.loadingPage = loading;
  }

  @Mutation
  private RESET_FILTER() {
    this.data.integratorMovementFilter = initializeFilter();
  }

  @Mutation
  private SET_FILTER(filter: Partial<IntegratorMovementFilter>) {
    this.data.integratorMovementFilter = {
      ...this.data.integratorMovementFilter,
      ...filter,
    }
  }

  @Mutation
  private SET_TOTAL_PAGES(totalPages: number) {
    this.data.totalPages = totalPages;
  }

  @Mutation
  private SET_TOTAL_ELEMENTS(totalElements: number) {
    this.data.totalElements = totalElements;
  }

  @Mutation
  private SET_INTEGRATOR_MOVEMENTS(data: IntegratorMovement[]) {
    this.data.integratorMovements = data;
  }
}

export default getModule(IntegratorMovementsStore);
